import { useState, useEffect } from 'react'
import { Settings, Save, Building2, Brain, Eye, EyeOff } from 'lucide-react'
import api from '../../../core/services/api'
import { Toast } from '../../../components/Toast'
import { TariffeConfigurazione, Tariffa } from '../../../types/offerte'
import { RUOLI_PERSONALE } from '../../../types/personale'
import { TIPOLOGIE_STRUMENTO } from '../../../types/strumenti'

interface DatiLegali {
  p_iva: string
  codice_fiscale: string
  pec: string
  sito_web: string
  sede_legale_via: string
  sede_legale_citta: string
  sede_legale_cap: string
  sede_legale_provincia: string
  rappresentante_legale: string
  rappresentante_legale_cf: string
  name: string
  email: string
  phone: string
  address: string
}

export default function ConfigurazionePage() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [margine, setMargine] = useState('20')
  const [tariffe, setTariffe] = useState<Tariffa[]>([])
  const [tariffeForm, setTariffeForm] = useState<Record<string, string>>({})
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null)

  // Dati legali tenant
  const [datiLegali, setDatiLegali] = useState<DatiLegali>({
    p_iva: '', codice_fiscale: '', pec: '', sito_web: '',
    sede_legale_via: '', sede_legale_citta: '', sede_legale_cap: '', sede_legale_provincia: '',
    rappresentante_legale: '', rappresentante_legale_cf: '',
    name: '', email: '', phone: '', address: '',
  })
  const [savingLegali, setSavingLegali] = useState(false)

  // AI API Key
  const [aiApiKey, setAiApiKey] = useState('')
  const [aiKeyConfigured, setAiKeyConfigured] = useState(false)
  const [aiKeyMasked, setAiKeyMasked] = useState('')
  const [showAiKey, setShowAiKey] = useState(false)
  const [savingAiKey, setSavingAiKey] = useState(false)

  useEffect(() => {
    loadConfigurazione()
    loadDatiLegali()
    loadAiApiKey()
  }, [])

  const loadConfigurazione = async () => {
    try {
      setLoading(true)
      const res = await api.get<TariffeConfigurazione>('/v1/offerte/configurazione/tariffe')
      const allTariffe = [...res.data.personale, ...res.data.mezzi]
      setTariffe(allTariffe)
      setMargine(String(res.data.margine_desiderato))

      const formValues: Record<string, string> = {}
      allTariffe.forEach(t => {
        formValues[t.chiave] = String(t.tariffa)
      })
      setTariffeForm(formValues)
    } catch (err) {
      console.error('Errore caricamento:', err)
      setToast({ message: 'Errore nel caricamento della configurazione', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const loadDatiLegali = async () => {
    try {
      const res = await api.get<DatiLegali>('/v1/anagrafiche/tenant/dati-legali')
      setDatiLegali({
        p_iva: res.data.p_iva || '',
        codice_fiscale: res.data.codice_fiscale || '',
        pec: res.data.pec || '',
        sito_web: res.data.sito_web || '',
        sede_legale_via: res.data.sede_legale_via || '',
        sede_legale_citta: res.data.sede_legale_citta || '',
        sede_legale_cap: res.data.sede_legale_cap || '',
        sede_legale_provincia: res.data.sede_legale_provincia || '',
        rappresentante_legale: res.data.rappresentante_legale || '',
        rappresentante_legale_cf: res.data.rappresentante_legale_cf || '',
        name: res.data.name || '',
        email: res.data.email || '',
        phone: res.data.phone || '',
        address: res.data.address || '',
      })
    } catch {
      // Silenzioso
    }
  }

  const loadAiApiKey = async () => {
    try {
      const res = await api.get('/v1/anagrafiche/tenant/settings/ai-api-key')
      setAiKeyConfigured(res.data.configured)
      setAiKeyMasked(res.data.ai_api_key || '')
    } catch {
      // Silenzioso — l'utente potrebbe non essere admin
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)

      const personalePayload = tariffe
        .filter(t => t.tipo === 'personale')
        .map(t => ({ chiave: t.chiave, tariffa: parseFloat(tariffeForm[t.chiave]) || 0 }))

      const mezziPayload = tariffe
        .filter(t => t.tipo === 'mezzo')
        .map(t => ({ chiave: t.chiave, tariffa: parseFloat(tariffeForm[t.chiave]) || 0 }))

      await api.put('/v1/offerte/configurazione/tariffe', {
        margine_desiderato: parseFloat(margine) || 20,
        personale: personalePayload,
        mezzi: mezziPayload,
      })
      setToast({ message: 'Configurazione salvata con successo', type: 'success' })
    } catch (err) {
      console.error('Errore salvataggio:', err)
      setToast({ message: 'Errore nel salvataggio', type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleSaveLegali = async () => {
    try {
      setSavingLegali(true)
      await api.put('/v1/anagrafiche/tenant/dati-legali', {
        p_iva: datiLegali.p_iva || null,
        codice_fiscale: datiLegali.codice_fiscale || null,
        pec: datiLegali.pec || null,
        sito_web: datiLegali.sito_web || null,
        sede_legale_via: datiLegali.sede_legale_via || null,
        sede_legale_citta: datiLegali.sede_legale_citta || null,
        sede_legale_cap: datiLegali.sede_legale_cap || null,
        sede_legale_provincia: datiLegali.sede_legale_provincia || null,
        rappresentante_legale: datiLegali.rappresentante_legale || null,
        rappresentante_legale_cf: datiLegali.rappresentante_legale_cf || null,
      })
      setToast({ message: 'Dati legali salvati', type: 'success' })
    } catch {
      setToast({ message: 'Errore salvataggio dati legali', type: 'error' })
    } finally {
      setSavingLegali(false)
    }
  }

  const handleSaveAiKey = async () => {
    try {
      setSavingAiKey(true)
      await api.put('/v1/anagrafiche/tenant/settings/ai-api-key', {
        ai_api_key: aiApiKey,
      })
      setToast({ message: 'Chiave API AI salvata', type: 'success' })
      setAiKeyConfigured(true)
      // Ricarica la versione mascherata
      await loadAiApiKey()
      setAiApiKey('')
      setShowAiKey(false)
    } catch {
      setToast({ message: 'Errore salvataggio chiave API', type: 'error' })
    } finally {
      setSavingAiKey(false)
    }
  }

  const updateLegali = (field: keyof DatiLegali, value: string) => {
    setDatiLegali(prev => ({ ...prev, [field]: value }))
  }

  const personaleTariffe = tariffe
    .filter(t => t.tipo === 'personale')
    .sort((a, b) => RUOLI_PERSONALE.indexOf(a.chiave as any) - RUOLI_PERSONALE.indexOf(b.chiave as any))
  const mezziTariffe = tariffe
    .filter(t => t.tipo === 'mezzo')
    .sort((a, b) => TIPOLOGIE_STRUMENTO.indexOf(a.chiave as any) - TIPOLOGIE_STRUMENTO.indexOf(b.chiave as any))

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Caricamento...</div>
      </div>
    )
  }

  return (
    <div>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-6 h-6" />
            Configurazione
          </h1>
          <p className="text-gray-500">Tariffe, dati aziendali e integrazioni AI</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          <Save className="w-5 h-5" />
          {saving ? 'Salvataggio...' : 'Salva Tariffe'}
        </button>
      </div>

      <div className="space-y-6 max-w-3xl">

        {/* === CHIAVE API AI === */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Brain className="w-5 h-5 text-indigo-500" />
                Intelligenza Artificiale
              </h3>
              <p className="text-sm text-gray-500">
                Chiave API Anthropic per analisi documenti, generazione contratti e fascicoli
              </p>
            </div>
            <div className="flex items-center gap-2">
              {aiKeyConfigured && !aiApiKey && (
                <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                  Configurata
                </span>
              )}
            </div>
          </div>

          <div className="space-y-3">
            {aiKeyConfigured && !aiApiKey && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="font-mono bg-gray-100 px-2 py-1 rounded text-xs">{aiKeyMasked}</span>
              </div>
            )}

            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type={showAiKey ? 'text' : 'password'}
                  value={aiApiKey}
                  onChange={(e) => setAiApiKey(e.target.value)}
                  placeholder={aiKeyConfigured ? 'Inserisci nuova chiave per sostituire...' : 'sk-ant-api03-...'}
                  className="w-full px-3 py-2 pr-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowAiKey(!showAiKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showAiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <button
                onClick={handleSaveAiKey}
                disabled={savingAiKey || !aiApiKey.trim()}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {savingAiKey ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>

            <p className="text-xs text-gray-400">
              Utilizzata per: analisi fonti fascicoli, generazione contratti, analisi template documenti
            </p>
          </div>
        </div>

        {/* === DATI LEGALI AZIENDA === */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-purple-500" />
                Dati Legali Azienda
              </h3>
              <p className="text-sm text-gray-500">Necessari per la generazione di contratti</p>
            </div>
            <button
              onClick={handleSaveLegali}
              disabled={savingLegali}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {savingLegali ? 'Salvataggio...' : 'Salva'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Partita IVA</label>
              <input
                type="text"
                value={datiLegali.p_iva}
                onChange={(e) => updateLegali('p_iva', e.target.value)}
                placeholder="IT01234567890"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Codice Fiscale</label>
              <input
                type="text"
                value={datiLegali.codice_fiscale}
                onChange={(e) => updateLegali('codice_fiscale', e.target.value)}
                placeholder="01234567890"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">PEC</label>
              <input
                type="email"
                value={datiLegali.pec}
                onChange={(e) => updateLegali('pec', e.target.value)}
                placeholder="azienda@pec.it"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Sito Web</label>
              <input
                type="text"
                value={datiLegali.sito_web}
                onChange={(e) => updateLegali('sito_web', e.target.value)}
                placeholder="https://www.azienda.it"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
              />
            </div>
          </div>

          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Sede Legale</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <input
                  type="text"
                  value={datiLegali.sede_legale_via}
                  onChange={(e) => updateLegali('sede_legale_via', e.target.value)}
                  placeholder="Via/Piazza e numero civico"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                />
              </div>
              <div>
                <input
                  type="text"
                  value={datiLegali.sede_legale_citta}
                  onChange={(e) => updateLegali('sede_legale_citta', e.target.value)}
                  placeholder="Citta'"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                />
              </div>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={datiLegali.sede_legale_cap}
                  onChange={(e) => updateLegali('sede_legale_cap', e.target.value)}
                  placeholder="CAP"
                  className="w-24 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                />
                <input
                  type="text"
                  value={datiLegali.sede_legale_provincia}
                  onChange={(e) => updateLegali('sede_legale_provincia', e.target.value.toUpperCase().slice(0, 2))}
                  placeholder="Prov."
                  maxLength={2}
                  className="w-20 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm uppercase"
                />
              </div>
            </div>
          </div>

          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Rappresentante Legale</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <input
                  type="text"
                  value={datiLegali.rappresentante_legale}
                  onChange={(e) => updateLegali('rappresentante_legale', e.target.value)}
                  placeholder="Nome e Cognome"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                />
              </div>
              <div>
                <input
                  type="text"
                  value={datiLegali.rappresentante_legale_cf}
                  onChange={(e) => updateLegali('rappresentante_legale_cf', e.target.value.toUpperCase())}
                  placeholder="Codice Fiscale"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm uppercase"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Margine Desiderato */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Margine Desiderato</h3>
          <p className="text-sm text-gray-500 mb-4">
            Percentuale minima di profitto. Le offerte sotto questa soglia mostreranno un avviso.
          </p>
          <div className="relative max-w-xs">
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={margine}
              onChange={(e) => setMargine(e.target.value)}
              className="w-full pl-4 pr-8 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
          </div>
        </div>

        {/* Tariffe Personale */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Tariffe Personale</h3>
          <p className="text-sm text-gray-500 mb-4">Costo giornaliero per ruolo</p>
          <div className="space-y-3">
            {personaleTariffe.map(t => (
              <div key={t.id} className="flex items-center gap-4">
                <span className="w-48 text-sm font-medium text-gray-700">{t.chiave}</span>
                <div className="relative flex-1 max-w-xs">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">€</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={tariffeForm[t.chiave] ?? '0'}
                    onChange={(e) => setTariffeForm({ ...tariffeForm, [t.chiave]: e.target.value })}
                    className="w-full pl-8 pr-16 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">/{t.unita}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tariffe Mezzi */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Tariffe Mezzi e Strumenti</h3>
          <p className="text-sm text-gray-500 mb-4">Costo per tipologia (al km per veicoli, al giorno per strumenti/macchinari)</p>
          <div className="space-y-3">
            {mezziTariffe.map(t => (
              <div key={t.id} className="flex items-center gap-4">
                <span className="w-48 text-sm font-medium text-gray-700">{t.chiave}</span>
                <div className="relative flex-1 max-w-xs">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">€</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={tariffeForm[t.chiave] ?? '0'}
                    onChange={(e) => setTariffeForm({ ...tariffeForm, [t.chiave]: e.target.value })}
                    className="w-full pl-8 pr-16 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">/{t.unita}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Info */}
        <div className="bg-blue-50 rounded-xl p-6">
          <h4 className="font-medium text-blue-900 mb-2">Come funzionano i calcoli</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>Costo Personale</strong> = Giorni x Tariffa giornaliera del ruolo</li>
            <li>• <strong>Costo Mezzi</strong> = Quantita x Tariffa della tipologia (km o giorni)</li>
            <li>• <strong>Costi Diretti</strong> = Importo inserito manualmente</li>
            <li>• <strong>Margine</strong> = (Valore - Costi) / Valore x 100</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
